import React, { useRef, useState, useCallback } from "react";
import { CameraIcon as Camera, UploadIcon as Upload, XIcon as X, ArrowsClockwiseIcon as ArrowsClockwise, ImageSquareIcon as ImageSquare } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { photoUrl, formatApiError } from "@/lib/utils-app";
import { isNativeApp } from "@/lib/nativeBridge";
import { takeNativePhoto, haptic } from "@/lib/native";

/**
 * Mobile-friendly photo uploader with:
 *  • Camera capture (capture="environment" — opens rear camera on mobile)
 *  • File picker (multi-select)
 *  • Client-side compression to ≤2000px max edge, ~0.85 quality JPEG
 *    (cuts a typical 8MP phone photo from 4MB → ~400KB before upload)
 *  • Drag-to-reorder (HTML5 native, also works with touch via fallback)
 *  • Remove button per photo
 *  • Live "uploading 2/5" progress
 *
 * Backend contract is unchanged: each file POSTed to `/api/upload` →
 * receives `{storage_path}`. The parent owns the `photos` array.
 */
export default function PhotoUploader({ photos, onChange, max = 12 }) {
  const galleryRef = useRef(null);
  const dragSrc = useRef(null);
  const [progress, setProgress] = useState(null); // { done, total }

  const compress = useCallback(async (file) => {
    // Skip non-images and tiny files (already small).
    if (!file.type.startsWith("image/") || file.size < 600 * 1024) return file;
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
      const maxEdge = 2000;
      let { width, height } = bitmap;
      const ratio = Math.min(1, maxEdge / Math.max(width, height));
      width = Math.round(width * ratio);
      height = Math.round(height * ratio);
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(bitmap, 0, 0, width, height);
      const blob = await new Promise((res) =>
        canvas.toBlob(res, "image/jpeg", 0.85)
      );
      if (!blob || blob.size >= file.size) return file;
      return new File([blob], file.name.replace(/\.\w+$/, "") + ".jpg", {
        type: "image/jpeg",
        lastModified: Date.now(),
      });
    } catch {
      // Compression failed — fall back to original.
      return file;
    }
  }, []);

  const processFiles = async (incoming) => {
    if (incoming.length === 0) return;
    const remaining = Math.max(0, max - photos.length);
    if (remaining === 0) {
      toast.error(`Already at the ${max}-photo limit. Remove one to add more.`);
      return;
    }
    const files = incoming.slice(0, remaining);
    if (files.length < incoming.length) {
      toast.message(`Only adding the first ${remaining} — at the ${max}-photo limit.`);
    }
    setProgress({ done: 0, total: files.length });
    const paths = [];
    try {
      for (let i = 0; i < files.length; i++) {
        const compressed = await compress(files[i]);
        const fd = new FormData();
        fd.append("file", compressed);
        const { data } = await api.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        paths.push(data.storage_path);
        setProgress({ done: i + 1, total: files.length });
      }
      onChange([...photos, ...paths]);
      haptic("light");
      toast.success(`${paths.length} photo${paths.length === 1 ? "" : "s"} added`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setProgress(null);
    }
  };

  const onFiles = async (e) => {
    const incoming = Array.from(e.target.files || []);
    e.target.value = ""; // allow same file re-pick after remove
    await processFiles(incoming);
  };

  // Native camera / photo-library via the system action sheet.
  const onNativeCamera = async () => {
    const file = await takeNativePhoto();
    if (file) await processFiles([file]);
  };

  const remove = (i) => {
    onChange(photos.filter((_, idx) => idx !== i));
  };

  const moveTo = (from, to) => {
    if (from === to || from < 0 || to < 0 || from >= photos.length || to >= photos.length) return;
    const next = photos.slice();
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    onChange(next);
  };

  const onDragStart = (i) => (e) => {
    dragSrc.current = i;
    e.dataTransfer.effectAllowed = "move";
    // Some browsers need text data set to start drag.
    e.dataTransfer.setData("text/plain", String(i));
  };
  const onDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };
  const onDrop = (i) => (e) => {
    e.preventDefault();
    const from = dragSrc.current ?? Number(e.dataTransfer.getData("text/plain"));
    moveTo(Number.isFinite(from) ? from : 0, i);
    dragSrc.current = null;
  };

  return (
    <div data-testid="photo-uploader">
      <div
        ref={galleryRef}
        className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-3"
      >
        {photos.map((p, i) => (
          <div
            key={`${p}-${i}`}
            draggable
            onDragStart={onDragStart(i)}
            onDragOver={onDragOver}
            onDrop={onDrop(i)}
            className="relative aspect-square rounded-lg overflow-hidden bg-[#FAFAFA] border border-[#E5E5E5] group cursor-grab active:cursor-grabbing"
            data-testid={`photo-tile-${i}`}
          >
            <img
              src={photoUrl(p)}
              alt=""
              className="w-full h-full object-cover pointer-events-none"
              loading="lazy"
            />
            {i === 0 && (
              <span className="absolute top-1 left-1 bg-[#C9A227] text-ink text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded">
                Cover
              </span>
            )}
            <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {i > 0 && (
                <button
                  type="button"
                  onClick={() => moveTo(i, i - 1)}
                  className="bg-white/95 rounded-full px-1 py-1 text-[10px] hover:bg-white shadow-sm"
                  data-testid={`photo-up-${i}`}
                  title="Move up"
                >
                  <ArrowsClockwise size={11} weight="bold" />
                </button>
              )}
              <button
                type="button"
                onClick={() => remove(i)}
                className="bg-white/95 rounded-full p-1 hover:bg-white shadow-sm"
                data-testid={`photo-remove-${i}`}
                title="Remove"
              >
                <X size={12} weight="bold" />
              </button>
            </div>
          </div>
        ))}

        {photos.length < max && (
          <>
            {/* Camera capture — native action sheet inside the app shell,
                capture="environment" file input on the mobile web. */}
            {isNativeApp() ? (
              <button
                type="button"
                onClick={onNativeCamera}
                className="aspect-square border-2 border-dashed border-[#E5E5E5] rounded-lg flex flex-col items-center justify-center cursor-pointer hover:bg-[#FAFAFA] transition-colors text-[#6B6B6B]"
                data-testid="photo-native-camera-btn"
              >
                <Camera size={22} weight="duotone" className="mb-1" />
                <span className="text-[11px] font-bold uppercase tracking-wider">Camera</span>
              </button>
            ) : (
            <label
              className="aspect-square border-2 border-dashed border-[#E5E5E5] rounded-lg flex flex-col items-center justify-center cursor-pointer hover:bg-[#FAFAFA] transition-colors text-[#6B6B6B]"
              data-testid="photo-camera-label"
            >
              <Camera size={22} weight="duotone" className="mb-1" />
              <span className="text-[11px] font-bold uppercase tracking-wider">Camera</span>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={onFiles}
                data-testid="photo-camera-input"
              />
            </label>
            )}
            {/* Multi-file picker. */}
            <label
              className="aspect-square border-2 border-dashed border-[#E5E5E5] rounded-lg flex flex-col items-center justify-center cursor-pointer hover:bg-[#FAFAFA] transition-colors text-[#6B6B6B]"
              data-testid="photo-upload-label"
            >
              <Upload size={22} weight="duotone" className="mb-1" />
              <span className="text-[11px] font-bold uppercase tracking-wider">Upload</span>
              <span className="text-[10px] text-[#8B8B8B]">Pick multiple</span>
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={onFiles}
                data-testid="photo-upload-input"
              />
            </label>
          </>
        )}
      </div>

      <div className="flex items-center justify-between text-[11px] text-[#6B6B6B]">
        <p className="flex items-center gap-1.5">
          <ImageSquare size={12} />
          {photos.length}/{max} photos · drag tiles to reorder · first tile is the cover
        </p>
        {progress && (
          <p data-testid="photo-progress" className="font-mono">
            Uploading {progress.done}/{progress.total}…
          </p>
        )}
      </div>
    </div>
  );
}
